import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation } from "react-router-dom";

import BuyButton from "./BuyButton";
import PromotionScratchCard, { PROMOTION_SCRATCH_ENABLED } from "./PromotionScratchCard";
import { getProduct } from "../util/api/productApi";

interface ProductPromotion {
  product_id: string;
  product_name: string;
  code: string;
  percentage: number;
  title: string;
  message: string;
  image_url: string | null;
  image_alt: string | null;
}

interface PromotionProduct {
  name: string;
  metadata: { product_id?: string };
  default_price: { id: string } | null;
  owned?: boolean;
}

interface PromotionState {
  key: string;
  promotion: ProductPromotion;
  product?: PromotionProduct;
  productError?: boolean;
}

// Product-specific links are resolved independently of the product-page layout.
export default function ProductPromotionPopup() {
  const { pathname, search } = useLocation();
  const productId = /^\/product\/([a-z0-9][a-z0-9-]*)\/?$/.exec(pathname)?.[1];
  const codes = new URLSearchParams(search).getAll("promo");
  const code = codes.length === 1 && /^[A-Za-z0-9]{1,32}$/.test(codes[0]) ? codes[0] : undefined;
  const key = productId && code ? `${productId}:${code.toUpperCase()}` : "";
  const [state, setState] = useState<PromotionState>();
  const [dismissedKey, setDismissedKey] = useState("");
  const [revealedKey, setRevealedKey] = useState("");
  const [failedImage, setFailedImage] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let dismissed = false;
    let revealed = false;
    try {
      dismissed = sessionStorage.getItem(`product-promotion-code-dismissed:${key}`) === "true";
      revealed = sessionStorage.getItem(`product-promotion-code-revealed:${key}`) === "true";
    } catch {
      // The reminder still works when browser storage is unavailable.
    }
    setDismissedKey(dismissed ? key : "");
    setRevealedKey(revealed ? key : "");
  }, [key]);

  useEffect(() => {
    let active = true;
    setState(undefined);
    setFailedImage("");
    if (!productId || !code) return () => { active = false; };

    axios.get<ProductPromotion>(`/api/promotions/${encodeURIComponent(productId)}/${encodeURIComponent(code)}`)
      .then(async ({ data: promotion }) => {
        if (!active || promotion.product_id !== productId || promotion.code.toUpperCase() !== code.toUpperCase()) return;
        setState({ key, promotion });
        try {
          const response = await getProduct(productId);
          const product: PromotionProduct = response.data;
          if (product?.metadata?.product_id !== productId || !product.default_price?.id) throw new Error("Product pricing unavailable");
          if (active) setState({ key, promotion, product });
        } catch {
          if (active) setState({ key, promotion, productError: true });
        }
      }).catch(() => {
        // Invalid, unavailable, and expired promotions must not interrupt shopping.
      });

    return () => { active = false; };
  }, [productId, code, key, retry]);

  const current = state?.key === key ? state : undefined;
  const promotion = current?.promotion;
  const imageUrl = promotion?.image_url;
  const revealed = !PROMOTION_SCRATCH_ENABLED || revealedKey === key;
  const reveal = () => {
    setRevealedKey(key);
    try {
      sessionStorage.setItem(`product-promotion-code-revealed:${key}`, "true");
    } catch {
      // Revealing an offer never depends on browser storage.
    }
  };
  const close = () => {
    setDismissedKey(key);
    try {
      sessionStorage.setItem(`product-promotion-code-dismissed:${key}`, "true");
    } catch {
      // Dismissal does not depend on browser storage being enabled.
    }
  };

  if (!promotion) return null;

  if (dismissedKey === key) return <Box component="aside" aria-label="Promotion reminder" sx={{
    position: "fixed", right: { xs: 12, sm: 20 }, left: { xs: 12, sm: "auto" },
    bottom: "calc(12px + env(safe-area-inset-bottom, 0px))", zIndex: (theme) => theme.zIndex.appBar - 1,
    maxWidth: 420, boxSizing: "border-box", display: "flex", alignItems: "center", gap: 2,
    px: 2, py: 1.25, borderRadius: 2, border: "1px solid rgba(255,255,255,0.16)",
    bgcolor: "#202020", color: "#fff", boxShadow: "0 4px 16px rgba(0,0,0,0.22)",
  }}>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      {revealed ? <Typography sx={{ fontSize: 12, lineHeight: 1.4, overflowWrap: "anywhere" }}>
        <Box component="strong" sx={{ whiteSpace: "nowrap" }}>{promotion.percentage}% off</Box>
        <Box component="span" sx={{ mx: 0.75, color: "#aaa" }}>·</Box>
        <Box component="span" sx={{ fontFamily: "monospace", userSelect: "all" }}>{promotion.code}</Box>
      </Typography> : <Typography sx={{ fontSize: 12, lineHeight: 1.4 }}>Your offer is ready</Typography>}
      <Button onClick={() => setDismissedKey("")} size="small" sx={{
        p: 0, minWidth: 0, mt: 0.25, color: "#bbb", fontSize: 11, lineHeight: 1.5,
        textTransform: "none", "&:hover": { color: "#fff", bgcolor: "transparent", textDecoration: "underline" },
      }}>{revealed ? "Offer details" : "Reveal offer"}</Button>
    </Box>
    {revealed && (current?.product ? <BuyButton product={current.product} text="Buy now" size="small" sx={{
      flexShrink: 0, borderRadius: 1.5, px: 1.5, py: 0.75, fontSize: 12, textTransform: "none",
    }} /> : current?.productError
      ? <Button color="inherit" size="small" onClick={() => setRetry(value => value + 1)}>Retry</Button>
      : <CircularProgress aria-label="Loading purchase options" size={18} sx={{ color: "#bbb" }} />)}
  </Box>;

  return <Dialog open onClose={close} fullWidth maxWidth="xs"
    aria-labelledby="product-promotion-title" aria-describedby="product-promotion-message"
    sx={{ zIndex: (theme) => theme.zIndex.tooltip + 1 }}
    PaperProps={{ elevation: 10, sx: {
      borderRadius: 3, border: "1px solid rgba(255,255,255,0.16)", color: "#fff",
      background: "radial-gradient(ellipse at top, #343434, #151515 75%)",
    } }}>
    <IconButton aria-label="Close promotion" onClick={close}
      sx={{ position: "absolute", right: 8, top: 8, zIndex: 1, color: "#fff", bgcolor: "rgba(0,0,0,0.55)", "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } }}>
      <CloseIcon />
    </IconButton>
    {imageUrl && failedImage !== imageUrl && <Box component="img" src={imageUrl}
      alt={promotion?.image_alt || promotion?.title || "Product promotion"}
      onError={() => setFailedImage(imageUrl)}
      sx={{ display: "block", width: "100%", maxHeight: 320, objectFit: "contain" }} />}
    <DialogContent sx={{ textAlign: "center", px: { xs: 3, sm: 5 }, pt: imageUrl && failedImage !== imageUrl ? 3 : 7, pb: 4 }}>
      <Typography component="p" sx={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "#bbb", mb: 2 }}>
        {promotion?.product_name} · {promotion?.percentage}% off
      </Typography>
      <Typography id="product-promotion-title" component="h2" sx={{ fontSize: { xs: 26, sm: 30 }, fontWeight: 700, lineHeight: 1.2, mb: 2, overflowWrap: "anywhere" }}>
        {promotion?.title}
      </Typography>
      <Typography id="product-promotion-message" sx={{ fontSize: 14, color: "#ccc", lineHeight: 1.6, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
        {promotion?.message}
      </Typography>
      <Box sx={{ position: "relative", display: "inline-block", mt: 3, px: 2, py: 1.25, border: "1px dashed #777", borderRadius: 1, maxWidth: "100%", boxSizing: "border-box" }}>
        <Typography aria-hidden={!revealed} sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: "#aaa", mb: 0.5 }}>Promotion code</Typography>
        <Typography aria-hidden={!revealed} sx={{ fontFamily: "monospace", fontSize: 15, fontWeight: 700, letterSpacing: "0.06em", overflowWrap: "anywhere" }}>{promotion?.code}</Typography>
        {PROMOTION_SCRATCH_ENABLED && <PromotionScratchCard key={key} percentage={promotion.percentage} code={promotion.code} revealed={revealed} onReveal={reveal} />}
      </Box>
      <Box sx={{ mt: 3 }}>
        {current?.product ? <BuyButton product={current.product} text="Buy now" sx={{ width: "100%", borderRadius: 2, py: 1.5 }} />
          : current?.productError ? <>
            <Typography sx={{ fontSize: 12, lineHeight: 1.4, mb: 1 }}>Purchase options are temporarily unavailable.</Typography>
            <Button color="inherit" variant="outlined" onClick={() => setRetry(value => value + 1)}>Retry</Button>
          </> : <CircularProgress aria-label="Loading purchase options" size={24} sx={{ color: "white" }} />}
      </Box>
    </DialogContent>
  </Dialog>;
}
